import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout, Spin, ConfigProvider, Result } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import theme from './theme/theme';

import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Groups from './pages/Groups';
import Tags from './pages/Tags';
import SopTemplates from './pages/SopTemplates';
import CustomerRelationshipAnalysis from './pages/CustomerRelationshipAnalysis';
import GroupRelationshipAnalysis from './pages/GroupRelationshipAnalysis';
import SopTemplateEdit from './pages/SopTemplateEdit/SopTemplateEdit';
import SopExecutions from './pages/SopExecutions';
import Callback from './pages/Callback';
import WeworkEmployees from './pages/WeworkEmployees';
import WeworkEmployeeGroups from './pages/WeworkEmployeeGroups';
import WeworkContactWay from './pages/WeworkContactWay';
import WeworkContactWayEdit from './pages/WeworkContactWayEdit';
import WeworkContactWayBatchEdit from './pages/WeworkContactWayBatchEdit';
import WeworkGroupJoinWay from './pages/WeworkGroupJoinWay';
import WeworkGroupJoinWayCreate from './pages/WeworkGroupJoinWayCreate';
import ScheduledSopTemplates from './pages/ScheduledSopTemplates/ScheduledSopTemplates';
import ScheduledSopTemplateEdit from './pages/ScheduledSopTemplates/ScheduledSopTemplateEdit';
import Materials from './pages/Materials';
import MaterialEdit from './pages/MaterialEdit';
import AiContent from './pages/AiContent';
import ProductEdit from './pages/ProductEdit';
import CustomerMassSend from './pages/CustomerMassSend';
import CustomerMassSendList from './pages/CustomerMassSendList';
import GroupMassSend from './pages/GroupMassSend';
import GroupMassSendList from './pages/GroupMassSendList';
import MomentMassSend from './pages/MomentMassSend';
import MomentMassSendList from './pages/MomentMassSendList';
import CustomerJourneyList from './pages/customer-journey/CustomerJourneyList';
import CustomerJourneyCreate from './pages/customer-journey/CustomerJourneyCreate';
import Stores from './pages/Stores';
import LbsContactWays from './pages/LbsContactWays';
import LbsContactWaysBatch from './pages/LbsContactWaysBatch';
import WeworkNonChannelWelcome from './pages/WeworkNonChannelWelcome';
import WeworkNonChannelWelcomeEdit from './pages/WeworkNonChannelWelcomeEdit';
import WeworkGroupWelcomeTemplates from './pages/WeworkGroupWelcomeTemplates';
import WeworkGroupWelcomeTemplateEdit from './pages/WeworkGroupWelcomeTemplateEdit';
import Monitoring from './pages/Monitoring';

import AppLayout from './components/AppLayout';
import { AuthProvider, useAuth } from './utils/auth';
import './App.css';

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, redirectToStandardLogin } = useAuth();
  const location = useLocation();

  // 记录当前URL，以便登录后重定向回来
  useEffect(() => {
    // 检查是否是回调路径
    const isCallbackPath = 
      location.pathname.includes('/auth/callback') || 
      location.pathname.includes('/api/auth/callback') || 
      location.pathname === '/callback';
      
    if (!isAuthenticated && !loading && !isCallbackPath) {
      // 将当前路径保存到localStorage, 登录后可以重定向回来
      localStorage.setItem('redirectPath', location.pathname);
    }
  }, [isAuthenticated, loading, location]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  // 检查是否是回调路径
  const isCallbackPath = 
    location.pathname.includes('/auth/callback') || 
    location.pathname.includes('/api/auth/callback') || 
    location.pathname === '/callback';
    
  // 如果用户未登录且不在回调路径，则重定向到登录页面
  if (!isAuthenticated && !isCallbackPath) {
    // 直接重定向到Casdoor登录，而不是本地登录页面
    redirectToStandardLogin();
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="正在跳转到登录页..." />
      </div>
    );
  }

  return children;
};

// 404页面组件
const NotFound = () => (
  <Result
    status="404"
    title="404"
    subTitle="很抱歉，您访问的页面不存在。"
    extra={<Navigate to="/" replace />}
  />
);

// 隐藏的重定向组件
const SrmRedirect = () => {
  useEffect(() => {
    // TODO: 替换为实际的目标链接
    const targetUrl = 'https://nocode.host/aunzjl'; // 这里需要替换为你要跳转到的实际链接
    window.location.href = targetUrl;
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Spin size="large" tip="正在跳转..." />
    </div>
  );
};

// 主应用组件
const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // 登录后尝试重定向到之前保存的路径
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/') {
      const redirectPath = localStorage.getItem('redirectPath');
      if (redirectPath) {
        localStorage.removeItem('redirectPath');
        // 可以在这里实现重定向逻辑
      }
    }
  }, [isAuthenticated, location]);

  return (
    <Routes>
      {/* 回调路由不需要认证保护 - 支持多种路径格式 */}
      <Route path="/api/auth/callback" element={<Callback />} />
      <Route path="/auth/callback" element={<Callback />} />
      <Route path="/callback" element={<Callback />} />
      
      {/* 隐藏的重定向路由 */}
      <Route path="/scrm" element={<SrmRedirect />} />
      
      {/* 根路径重定向到dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 所有业务页面都使用相同的AppLayout，但路径独立 */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
      </Route>
      
      <Route path="/monitor" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Monitoring />} />
      </Route>
      
      <Route path="/customers" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Customers />} />
      </Route>
      
      <Route path="/groups" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Groups />} />
      </Route>
      
      <Route path="/tags" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Tags />} />
      </Route>
      
      <Route path="/sop-templates" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<SopTemplates />} />
        <Route path=":id" element={<SopTemplateEdit />} />
      </Route>
      
      <Route path="/sop-template" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route path="create" element={<SopTemplateEdit />} />
        <Route path="edit/:id" element={<SopTemplateEdit />} />
      </Route>
      
      <Route path="/sop-executions" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<SopExecutions />} />
      </Route>
      
      <Route path="/scheduled-sop-templates" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ScheduledSopTemplates />} />
        <Route path=":id" element={<ScheduledSopTemplateEdit />} />
      </Route>
      
      <Route path="/ai-content" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AiContent />} />
        <Route path="history" element={<Navigate to="/ai-content?tab=history" replace />} />
      </Route>

      <Route path="/products" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/ai-content?tab=products" replace />} />
        <Route path="create" element={<ProductEdit />} />
        <Route path="edit/:id" element={<ProductEdit />} />
      </Route>

      <Route path="/campaigns" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/ai-content?tab=campaigns" replace />} />
      </Route>

      <Route path="/materials" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Materials />} />
        <Route path="create" element={<MaterialEdit />} />
        <Route path="edit/:id" element={<MaterialEdit />} />
      </Route>
      
      <Route path="/wework-employees" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WeworkEmployees />} />
      </Route>
      
      <Route path="/wework-employee-groups" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WeworkEmployeeGroups />} />
      </Route>
      
      <Route path="/wework-contact-way" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WeworkContactWay />} />
        <Route path="create" element={<WeworkContactWayEdit />} />
        <Route path="edit/:id" element={<WeworkContactWayEdit />} />
        <Route path="batch-edit" element={<WeworkContactWayBatchEdit />} />
      </Route>
      
      <Route path="/wework-group-join-way" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WeworkGroupJoinWay />} />
        <Route path="create" element={<WeworkGroupJoinWayCreate />} />
        <Route path="edit/:id" element={<WeworkGroupJoinWayCreate />} />
      </Route>
      
      <Route path="/customer-mass-send" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CustomerMassSendList />} />
        <Route path="create" element={<CustomerMassSend />} />
        <Route path="view/:taskId" element={<CustomerMassSend />} />
        <Route path="edit/:id" element={<CustomerMassSend />} />
      </Route>
      
      <Route path="/group-mass-send" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<GroupMassSendList />} />
        <Route path="create" element={<GroupMassSend />} />
        <Route path="view/:taskId" element={<GroupMassSend />} />
        <Route path="edit/:id" element={<GroupMassSend />} />
      </Route>
      
      <Route path="/moment-mass-send" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<MomentMassSendList />} />
        <Route path="create" element={<MomentMassSend />} />
        <Route path="view/:taskId" element={<MomentMassSend />} />
        <Route path="edit/:id" element={<MomentMassSend />} />
      </Route>
      
      <Route path="/customer-journey" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CustomerJourneyList />} />
        <Route path="create" element={<CustomerJourneyCreate />} />
        <Route path="edit/:id" element={<CustomerJourneyCreate />} />
      </Route>
      
      <Route path="/stores" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Stores />} />
      </Route>
      
      <Route path="/wework-non-channel-welcome" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WeworkNonChannelWelcome />} />
        <Route path="create" element={<WeworkNonChannelWelcomeEdit />} />
        <Route path="edit/:id" element={<WeworkNonChannelWelcomeEdit />} />
      </Route>

      <Route path="/wework-group-welcome-templates" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<WeworkGroupWelcomeTemplates />} />
        <Route path="create" element={<WeworkGroupWelcomeTemplateEdit />} />
        <Route path=":id" element={<WeworkGroupWelcomeTemplateEdit />} />
      </Route>
      
      <Route path="/lbs-contact-ways" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<LbsContactWays />} />
        <Route path="batch" element={<LbsContactWaysBatch />} />
        <Route path=":id/edit" element={<LbsContactWaysBatch />} />
      </Route>

      <Route path="/customer-relationship-analysis" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CustomerRelationshipAnalysis />} />
      </Route>

      <Route path="/group-relationship-analysis" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<GroupRelationshipAnalysis />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// 应用根组件
function App() {
  return (
    <ConfigProvider locale={zhCN} theme={theme}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App; 
